import { defineStore } from 'pinia'
import { computed, type Ref, ref } from 'vue'

import { ClientService, DataServiceFactory, EntraIDAuthService } from '@/factories'
import type { IClient, IClientContact, IClientIndex, IDropdown, IStoreEntity } from '@/interfaces'
import { Client } from '@/models'
import { EntraIDAuthConfig } from '@/services'
import { type IFullStore, useAuthStore, useBaseStore, useCommonStore } from '@/stores'

export interface IClientStore<T extends IStoreEntity, TI = T> extends IFullStore<T, TI> {
	contacts: Ref<IClientContact[]>
	clientTypeDropdown: Ref<IDropdown[]>
	clientDropdown: Ref<IDropdown[]>
	clientDropdownExpanded: Ref<IDropdown[]>
	clientContactDropdown: Ref<IDropdown[]>
	clientService: ClientService
	authService: EntraIDAuthService

	pagedLeads: Ref<TI[]>
	pagedClients: Ref<TI[]>

	addNew: () => T
	getBySubJobId: (subjobId: string | number) => Promise<T>
	setClientContacts: (id: string) => Promise<IClientContact[]>
	getSalesPerson: (id: string) => Promise<number>
}

const authService = DataServiceFactory.createAuthService('EntraIDAuth', EntraIDAuthConfig) as EntraIDAuthService
const clientService = DataServiceFactory.createService('Client', authService) as ClientService

export const useClientStore = defineStore<'client-store', IClientStore<IClient, IClientIndex>>('client-store', () => {
	const {
		id,
		records,
		activeRecord,
		searchTerm,
		preFilters,
		currentPage,
		sortCriteria,
		storeActive,
		isDuplicating,
		activeRecordSavedHash,
		indexView,
		indexFilter,
		indexParams,
		viewList,
		filterList,
		pageSize,

		elevatedPermissions,
		currentFormHash,
		currentFormDirty,

		// Column filter methods
		columnFilters,
		activeColumnFilters,
		hasActiveColumnFilters,
		activeColumnFilterCount,
		setColumnFilter,
		clearColumnFilter,
		clearAllColumnFilters,
		getColumnFilter,
		hasColumnFilter,
		preFilteredRecords,
		searchedRecords,
		searchRecordIndex,
		searchedRecordLength,
		currentPages,
		totalPages,
		recordCount,
		previousRecord,
		nextRecord,
		orderedRecords,
		pagedRecords,

		canRead,
		canEdit,
		canAdd,
		clearActive,
		clearSearch,
		calcHash
	} = useBaseStore<IClient, 'Client', IClientIndex>('Client')

	const getAuthStore = computed(() => {
		return useAuthStore()
	})

	const contacts = ref<IClientContact[]>([])

	const pagedLeads = computed(() => {
		return pagedRecords.value.filter((r) => r.type === 'Lead')
	})

	const pagedClients = computed(() => {
		return pagedRecords.value.filter((r) => r.type === 'Client')
	})

	const clientTypeDropdown = computed(() => {
		return [
			{ name: 'Lead', code: 'Lead' },
			{ name: 'Client', code: 'Client' }
		]
	})

	const clientDropdown = computed(() => {
		return records.value.map((r) => {
			return { name: r.name || 'No Name', code: r.id }
		})
	})

	const clientDropdownExpanded = computed(() => {
		const commonStore = useCommonStore()
		const mySalesPersons = commonStore.mySalesPersons

		// if (!records.value.length) {
		// 	await getIndex()
		// 	// console.log('ClientStore: Records loaded')
		// }

		return (
			records.value
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				.filter((r) => mySalesPersons.some((sp: any) => sp.name === r.salesPerson))
				.map((r) => {
					return { name: `${r.id} - ${r.name || 'No Name'}`, code: r.id }
				})
		)
	})

	const clientContactDropdown = computed(() => {
		return contacts.value.map((r) => {
			return { name: r.id, code: r.recId }
		})
	})

	async function getIndex(): Promise<IClientIndex[]> {
		return clientService
			.getIndex()
			.then((data) => {
				// console.log('ClientStore: Records loaded', data)
				records.value = data
				return records.value
			})
			.catch((err) => {
				console.error(err)
				records.value = []
				return records.value
			})
	}

	async function get(id: string): Promise<Client> {
		if (id === '0') {
			return addNew()
		}
		return clientService
			.get(encodeURIComponent(id))
			.then((data) => {
				activeRecord.value = data
				activeRecordSavedHash.value = calcHash(data)
				return activeRecord.value
			})
			.catch((err) => {
				console.error(err)
				activeRecord.value = {} as Client
				return activeRecord.value
			})
	}

	function addNew(): Client {
		const record = new Client({} as Client)
		record.createBy = getAuthStore.value.userInfo.displayName
		activeRecord.value = record
		activeRecordSavedHash.value = calcHash(record)
		return activeRecord.value
	}

	async function getBySubJobId(subjobId: string | number): Promise<Client> {
		return clientService
			.getClientBySubJobId(subjobId)
			.then((data) => {
				activeRecord.value = data
				activeRecordSavedHash.value = calcHash(data)
				return activeRecord.value
			})
			.catch((err) => {
				console.error('Failed to fetch client by subjob ID:', err)
				activeRecord.value = {} as Client
				throw err
			})
	}

	async function save(client: Client): Promise<Client> {
		if (client.id === 'Code not assigned yet...') {
			return clientService
				.add(client)
				.then((data) => {
					activeRecord.value = data
					activeRecordSavedHash.value = calcHash(data)
					return activeRecord.value
				})
				.catch((err) => {
					console.error(err)
					throw err
				})
		} else {
			return clientService
				.update(client)
				.then((data) => {
					activeRecord.value = data
					activeRecordSavedHash.value = calcHash(data)
					return activeRecord.value
				})
				.catch((err) => {
					console.error(err)
					throw err
				})
		}
	}

	async function getSalesPerson(id: string): Promise<number> {
		const commonStore = useCommonStore()

		// Helper function to find the client by ID
		const findClient = (id: string) => records.value.find((r) => r.id === id)

		// Helper function to find the salesperson by name
		const findSalesPerson = (name: string) => commonStore.salespersons.find((sp) => sp.name === name)

		// Check if records need to be loaded
		if (!records.value.length) {
			await getIndex()
		}

		const client = findClient(id)
		if (!client) {
			throw new Error('Client not found')
		}

		const salesPerson = findSalesPerson(client.salesPerson)
		if (!salesPerson) {
			throw new Error('Sales Person not found')
		}

		return salesPerson.id
	}

	async function setClientContacts(id: string): Promise<IClientContact[]> {
		console.log('setClientContacts', id)
		return clientService.getClientContacts(id).then((data) => {
			contacts.value = data
			return contacts.value
		})
	}

	//TODO: Review a better way to load dependent data.
	//This is needed to load the dropdowns for the client form, used by the Jobs module.
	if (!records.value.length) {
		getIndex()
		console.log('ClientStore: Records loaded')
	}

	return {
		id,
		records,
		activeRecord,
		searchTerm,
		currentPage,
		preFilters,
		sortCriteria,
		storeActive,
		isDuplicating,
		activeRecordSavedHash,
		indexView,
		indexFilter,
		indexParams,
		pageSize,

		elevatedPermissions,
		currentFormHash,
		currentFormDirty,
		preFilteredRecords,
		searchedRecords,
		searchRecordIndex,
		searchedRecordLength,
		currentPages,
		totalPages,
		recordCount,
		previousRecord,
		nextRecord,
		orderedRecords,
		pagedRecords,

		pagedLeads,
		pagedClients,
		viewList,
		filterList,

		getAuthStore,
		contacts,
		clientTypeDropdown,
		clientDropdown,
		clientDropdownExpanded,
		clientContactDropdown,

		clientService,
		authService,
		canRead,
		canEdit,
		canAdd,
		getIndex,
		get,
		addNew,
		getBySubJobId,
		save,
		clearActive,
		clearSearch,
		calcHash,
		setClientContacts,
		getSalesPerson,

		// Column filter methods
		columnFilters,
		activeColumnFilters,
		hasActiveColumnFilters,
		activeColumnFilterCount,
		setColumnFilter,
		clearColumnFilter,
		clearAllColumnFilters,
		getColumnFilter,
		hasColumnFilter
	}
})
